import React, { useState, useRef, createRef } from 'react';
import styled from 'styled-components';
import MyColor from 'utils/MyColor';

/* 주의사항 */
// 1. React.Fragment를 ESignDocumentTemplate 자식(자손 미포함) 요소로 사용하지 않습니다.
//    사용할 경우, 페이지 분할 로직이 정상 작동하지 않습니다.
//    (Fragment인 경우 fletten하고 있지만, 커스텀 컴포넌트는 Fragment로 인식되지 않습니다)
// 2. ESignDocumentTemplate 자식 요소가 많아질수록, 페이지 분할 성능은 떨어집니다.
//    따라서, 가능한 적은 자식 요소를 사용합니다.

const PAGE_HEIGHT = 1414.52 - 80 - 80; // 문서 높이 - 헤더 높이 - 푸터 높이 = 1254.52

// 문서 푸터 회사 정보 — 두 렌더 브랜치(모바일/분할 페이지)가 공유. 변경 시 여기만 수정
const FOOTER_LINE1 =
  '주식회사 꼬르륵 | 대표 : 안현숙 | 주소 : 충청남도 천안시 서북구 쌍용대로 133 502호(쌍용동)';
const FOOTER_LINE2 = '사업자등록번호 : 863-88-03560 | 고객센터 : 1833-4756 | 대표번호 : 1833-4756';
// const PAGE_HEIGHT = 100; // 테스트용

export default function ESignDocumentTemplate({
  children,
  isMobile = false,
  shouldSplitPages = false,
  setDocumentRefs,
}) {
  const signDate = new Date();
  const formattedDate = signDate
    ? `${signDate.getFullYear()}/${String(signDate.getMonth() + 1).padStart(2, '0')}/${String(
        signDate.getDate()
      ).padStart(2, '0')}`
    : '';

  // ---------- pdf & 웹 버전에서 페이지 분할 로직 ----------
  const [pages, setPages] = useState([]);
  const containerRef = useRef(null);
  const documentRefs = useRef(null);

  const flattenChildren = (children) => {
    const result = [];

    React.Children.forEach(children, (child) => {
      if (React.isValidElement(child) && child.type === React.Fragment) {
        // Fragment이면 재귀적으로 내부 자식 풀기
        result.push(...flattenChildren(child.props.children));
      } else if (
        child !== null &&
        child !== undefined &&
        typeof child !== 'boolean' &&
        child !== ''
      ) {
        // null/false/undefined/'' 는 DOM 노드를 만들지 않으므로 제외해야 함
        // (React.Children.toArray는 최상위에서 걸러주지만, Fragment 내부 자식은
        //  forEach가 null/false도 방문하므로 여기서 걸러야 DOM 인덱스와 어긋나지 않음.
        //  어긋나면 마지막 요소가 페이지 분할에서 누락됨)
        result.push(child);
      }
    });

    return result;
  };

  const splitPage = (element) => {
    if (isMobile || !shouldSplitPages) return;
    if (!element) return;

    const childElements = Array.from(element.children);
    const allChildren = flattenChildren(React.Children.toArray(children));

    const newPages = [];
    let currentPage = [];
    let heightSoFar = 0;

    childElements.forEach((child, idx) => {
      const style = getComputedStyle(child);
      const childHeight =
        child.getBoundingClientRect().height +
        parseFloat(style.marginTop) +
        parseFloat(style.marginBottom);

      // 보이는 내용이 없는 여백(gap)/빈 요소 여부
      const isSpacer =
        !(child.textContent || '').trim() && !child.querySelector('img, svg, canvas');

      // 여백 요소가 페이지 경계를 넘기는 경우: 새 페이지를 만들지 않고 건너뜀
      // (문서 끝 여백이 다음 페이지로 밀려나 헤더/푸터만 있는 빈 페이지가 생기는 문제 방지)
      if (isSpacer && heightSoFar + childHeight > PAGE_HEIGHT) {
        return;
      }

      // data-page-break 마커: 이 지점부터 강제로 새 페이지 시작
      const forcePageBreak = child.getAttribute && child.getAttribute('data-page-break') === 'true';

      if ((forcePageBreak || heightSoFar + childHeight > PAGE_HEIGHT) && currentPage.length > 0) {
        // 현재 페이지를 저장하고 새 페이지 시작
        newPages.push(currentPage);
        currentPage = [];
        heightSoFar = 0;
      }

      currentPage.push(allChildren[idx]);
      heightSoFar += childHeight;
    });

    if (currentPage.length > 0) newPages.push(currentPage);

    setPages(newPages);

    const newDocumentRefs = [];
    newPages.forEach(() => {
      newDocumentRefs.push(createRef());
    });
    documentRefs.current = newDocumentRefs;
    setDocumentRefs(newDocumentRefs);
  };

  const handleRef = (element) => {
    if (element && !containerRef.current) {
      // 렌더링 완료 후 페이지 분할
      setTimeout(() => {
        requestAnimationFrame(() => splitPage(element));
      }, 0);
      containerRef.current = element;
    }
  };

  // ------------------------------

  if (isMobile || !shouldSplitPages) {
    return (
      <Container $isMobile={isMobile}>
        <Inner $isMobile={isMobile}>
          {!isMobile && (
            <Header>
              <span style={{ fontSize: 40, fontWeight: 600, color: MyColor.brand40 }}>꼬르륵</span>
              <span style={{ fontSize: 13, fontWeight: 500, color: '#2C2C2C' }}>
                {formattedDate}
              </span>
            </Header>
          )}

          {children}
        </Inner>

        {!isMobile && (
          <Footer $isMobile={isMobile}>
            {FOOTER_LINE1}
            {'\n'}
            {FOOTER_LINE2}
          </Footer>
        )}
      </Container>
    );
  }

  return (
    <>
      {/* 실제 DOM 렌더링용, 페이지 나누기 위해 참조 */}
      <HiddenContainer ref={handleRef} $isMobile={isMobile}>
        {children}
      </HiddenContainer>

      {/* 분할된 페이지 렌더링 */}
      {pages.map((pageChildren, pageIndex) => (
        <Container
          key={pageIndex}
          $isMobile={isMobile}
          ref={
            documentRefs.current && documentRefs.current[pageIndex]
              ? documentRefs.current[pageIndex]
              : null
          }
        >
          <Inner $isMobile={isMobile}>
            {!isMobile && (
              <Header>
                <span style={{ fontSize: 40, fontWeight: 600, color: MyColor.brand40 }}>
                  꼬르륵
                </span>
                <span style={{ fontSize: 13, fontWeight: 500, color: '#2C2C2C' }}>
                  {formattedDate}
                </span>
              </Header>
            )}
            {pageChildren}
          </Inner>
          {!isMobile && (
            <Footer $isMobile={isMobile}>
              {FOOTER_LINE1}
              {'\n'}
              {FOOTER_LINE2}
            </Footer>
          )}
        </Container>
      ))}
    </>
  );
}

const HiddenContainer = styled.div`
  position: absolute;
  visibility: hidden;
  width: ${({ $isMobile }) => ($isMobile ? '100%' : '1000px')};
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
  padding: 0 ${({ $isMobile }) => ($isMobile ? 0 : '62px')};
`;

const Container = styled.div`
  width: ${({ $isMobile }) => ($isMobile ? '100%' : '1000px')};
  min-height: ${({ $isMobile }) => ($isMobile ? 'auto' : '1414.52px')};
  background-color: #fff;
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
`;

const Inner = styled.div`
  padding: 0 ${({ $isMobile }) => ($isMobile ? 0 : '62px')};
  width: 100%;
`;

const Header = styled.div`
  height: 80px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Footer = styled.div`
  padding: 0 ${({ $isMobile }) => ($isMobile ? 0 : '62px')};
  height: 80px;
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 500;
  color: #2c2c2c;
  white-space: pre-line;
  width: 100%;
  margin-top: auto;
  border-top: #cfcfcf solid 2.5px;
`;
